import type React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import { Button, Container, SpacerRow } from '@zg-rentals/multibrand-ui';
import Section from '@rental-manager/components/Section';
import Modal from '@common/components/Modal';
import userPersonas from '@common/constants/userPersonas';
import FeaturePermissionTypes from '@rental-manager/constants/FeaturePermissionTypes';
import ProblemTriangle from '@zg-rentals/multibrand-ui/lib/icons/ProblemTriangle';
import { ExpandableDisclaimer } from '@zillow/zrs-comet';
import type { IStyledTextModel } from '@rental-manager/models/Types';
import DebouncedButton from '@common/components/DebouncedButton';
import './style.scss';

type RentalProtectionDisclaimers = Array<IStyledTextModel>;

type Props = {
  onClose: () => void;
  skipRentalProtectionOnboarding: ({ featureName, on }) => void;
  onBack: () => void;
  rentalProtectionDisclaimers: RentalProtectionDisclaimers;
};

const IneligibleLeaseSelected: React.FC<React.PropsWithChildren<Props>> = (props: Props): React.ReactElement => {
  const skipRentalProtectionOnboarding = () => {
    props.skipRentalProtectionOnboarding({
      featureName: FeaturePermissionTypes.RENT_GUARANTEE,
      on: false,
    });
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      header="Payment setup"
      onRequestClose={props.onClose}
      dataAutoTestId="landlord-rental-protection-ineligible-lease-selected"
    >
      {
        <Container className="Ineligible-Lease-Checklist">
          <Section>
            <ProblemTriangle className="centeredImg" colorType="action" size="lg" />
          </Section>
          <Section>
            <Text type="h4" className="txtC zillowInsuranceSubHeading" emphasize>
              Zillow Insurance Services
            </Text>
            <Text type="h4" className="txtC boldText">
              An eligible lease is required for Rental Protection
            </Text>
          </Section>
          <SpacerRow size="sm" />
          <Section>
            <Text>The lease you selected was ineligible for one of the following reasons:</Text>
            <ul>
              <li>It was signed with a tenant who you didn&apos;t mark eligible for Rental Protection</li>
              <li>It does not meet term requirements of 6-24 months</li>
              <li>
                You started your Rental Protection request or are setting up Zillow Payments after your lease start date
              </li>
            </ul>
          </Section>
          <Section>
            <Text emphasize>
              If you choose to continue with this lease, you won&apos;t be able to purchase Rental Protection.
            </Text>
          </Section>
          <SpacerRow size="sm" />
          <Section>
            <ExpandableDisclaimer disclaimerContent={props.rentalProtectionDisclaimers} title="Legal Disclaimer" />
          </Section>
          <SpacerRow size="sm" />
          <Section>
            <Button onClick={props.onBack} type="secondary" full>
              Back
            </Button>
          </Section>
          <Section>
            <DebouncedButton onClick={skipRentalProtectionOnboarding} type="primary" full>
              Continue
            </DebouncedButton>
          </Section>
        </Container>
      }
    </Modal>
  );
};

export default IneligibleLeaseSelected;
